/**
 * PDP Main — buy-box state & cart integration.
 *
 * Gift logic (mesh bag scaling): the bag is a $0 product. After adding bars,
 * we count ALL bars across the cart and reconcile the mesh bag line quantity
 * to the configured step (default 3/6/9 breakpoints → 1-2:1, 3-5:2, 6-8:3, 9+:4).
 * Bag line carries a hidden `_free_gift` line-item property so the cart drawer
 * can lock its stepper.
 *
 * No-discount variants (Mystery Bar): sell at their own price, never get the
 * tier %, and never count toward reaching a tier — matching the Shopify
 * automatic discounts, which exclude them.
 */

(function () {
  'use strict';

  const SELECTOR_ROOT = '[data-pdp-main]';
  const NO_DISCOUNT_RE = /mystery/i;

  function moneyFormat(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      try { return window.Shopify.formatMoney(Math.round(cents), fmt); } catch (e) {}
    }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  class PDPBuyBox {
    constructor(root) {
      this.root = root;
      this.buyBox = root.querySelector('[data-buy-box]');
      if (!this.buyBox) return;

      const tiersScript = this.buyBox.querySelector('[data-tiers-json]');
      try {
        this.tiers = JSON.parse((tiersScript && tiersScript.textContent.trim()) || '[]');
      } catch (e) {
        this.tiers = [];
      }

      this.unitNoun = (this.buyBox.dataset.unitNoun || 'bar').toLowerCase();
      this.unitNounPlural = (this.buyBox.dataset.unitNounPlural || (this.unitNoun + 's')).toLowerCase();

      // Free gift config — bag is $0; we set its line qty by total bars in cart.
      this.productId = parseInt(root.dataset.productId, 10) || null;
      this.freeGiftVariantId = root.dataset.freeGiftVariantId || null;
      this.giftBagSteps = (root.dataset.giftBagSteps || '3,6,9')
        .split(',').map((n) => parseInt(n, 10)).filter((n) => n > 0);

      // Cart-awareness — offset the tier ladder by qty of items with the same
      // product.type already in the cart. Shopify automatic discounts tier on
      // TOTAL cart qty, so the buy box should highlight the tier the customer
      // will actually get at checkout, not "as if starting from zero".
      this.productType = (this.buyBox.dataset.productType || '').toLowerCase();
      this.cartCount = 0;
      this._suppressUnlockFlash = false;

      this.state = {
        scents: {},
        clickedTier: null
      };

      this.dom = {
        tierRadios:     Array.from(this.buyBox.querySelectorAll('[data-tier-radio]')),
        tierOptions:    Array.from(this.buyBox.querySelectorAll('[data-tier-option]')),
        scentRows:      Array.from(this.buyBox.querySelectorAll('[data-scent-row]')),
        scentMeta:      this.buyBox.querySelector('[data-scent-meta]'),
        progressFill:   this.buyBox.querySelector('[data-scent-progress-fill]'),
        freeGift:       this.buyBox.querySelector('[data-free-gift]'),
        subtotalMeta:   this.buyBox.querySelector('[data-subtotal-meta]'),
        subtotalPrice:  this.buyBox.querySelector('[data-subtotal-price]'),
        subtotalSaved:  this.buyBox.querySelector('[data-subtotal-saved]'),
        cartNote:       this.buyBox.querySelector('[data-cart-note]'),
        addButton:      this.buyBox.querySelector('[data-add-to-bag]'),
        addLabel:       this.buyBox.querySelector('[data-add-to-bag-label]'),
        stickyCta:      root.querySelector('[data-sticky-cta]'),
        stickyMeta:     root.querySelector('[data-sticky-meta]'),
        stickyPrice:    root.querySelector('[data-sticky-price]'),
        stickyAdd:      root.querySelector('[data-sticky-add]'),
        galleryMain:    root.querySelector('[data-gallery-main]'),
        galleryThumbs:  Array.from(root.querySelectorAll('[data-gallery-thumb]')),
        galleryViewport: root.querySelector('[data-gallery-viewport]')
      };

      this.galleryImages = this.dom.galleryThumbs.map((t) => ({
        src: t.dataset.imageSrc, alt: t.dataset.imageAlt
      }));
      this.galleryIndex = 0;

      this.scrolledPastInline = false;

      this._baseVariantPrice = null;
      this.fillTierUnitPrices();
      this.bind();
      this.render();

      // Async cart sync — non-blocking. Page renders normally at cartCount=0
      // until this fetch lands, then re-renders with the offset. The
      // _suppressUnlockFlash flag prevents the tier-crossing animation from
      // firing on this passive sync (would falsely imply the customer just
      // unlocked a tier by adding items). No-discount items (Mystery) are
      // skipped — they don't count toward tiers at checkout.
      if (this.productType) {
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            const items = (cart && cart.items) || [];
            this.cartCount = items
              .filter((i) => (i.product_type || '').toLowerCase() === this.productType)
              .filter((i) => !NO_DISCOUNT_RE.test(i.variant_title || ''))
              .reduce((s, i) => s + (i.quantity || 0), 0);
            this._suppressUnlockFlash = true;
            this.render();
          })
          .catch(() => {});
      }
    }

    /* ─── BASE PRICE ──────────────────────────────────────────── */

    getBaseVariantPrice() {
      if (this._baseVariantPrice != null) return this._baseVariantPrice;
      const attr = parseInt(this.buyBox.dataset.basePrice, 10);
      if (attr > 0) { this._baseVariantPrice = attr; return attr; }
      let min = Infinity;
      this.dom.scentRows.forEach((row) => {
        if (this.isNoDiscount(row)) return;
        const p = parseInt(row.dataset.variantPrice, 10) || 0;
        if (p > 0 && p < min) min = p;
      });
      this._baseVariantPrice = min === Infinity ? 0 : min;
      return this._baseVariantPrice;
    }

    fillTierUnitPrices() {
      const base = this.getBaseVariantPrice();
      this.dom.tierOptions.forEach((opt) => {
        const idx = parseInt(opt.dataset.tierIndex, 10);
        const tier = this.tiers[idx];
        const unitEl = opt.querySelector('[data-tier-unit]');
        if (!tier || !unitEl) return;
        const unit = base * (1 - (tier.pct || 0) / 100);
        unitEl.textContent = moneyFormat(unit) + '/ea';
      });
    }

    /* ─── EVENT BINDING ───────────────────────────────────────── */

    bind() {
      this.dom.tierRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          this.state.clickedTier = parseInt(e.target.value, 10);
          this.render();
        });
      });

      this.buyBox.addEventListener('click', (e) => {
        const inc = e.target.closest('[data-scent-inc]');
        const dec = e.target.closest('[data-scent-dec]');
        if (inc) { this.updateScent(inc.closest('[data-scent-row]'), +1); }
        else if (dec) { this.updateScent(dec.closest('[data-scent-row]'), -1); }
      });

      this.dom.addButton && this.dom.addButton.addEventListener('click', () => this.addToBag());
      this.dom.stickyAdd && this.dom.stickyAdd.addEventListener('click', () => this.addToBag());

      const gallery = this.root.querySelector('[data-pdp-gallery]');
      if (gallery) {
        gallery.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-gallery-thumb]');
          if (!thumb) return;
          const idx = this.dom.galleryThumbs.indexOf(thumb);
          this.showGalleryIndex(idx);
          thumb.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        });
      }

      const vp = this.dom.galleryViewport;
      if (vp && this.galleryImages.length > 1) {
        let startX = 0, startY = 0, tracking = false;
        vp.addEventListener('touchstart', (e) => {
          if (!e.touches || !e.touches.length) return;
          startX = e.touches[0].clientX; startY = e.touches[0].clientY; tracking = true;
        }, { passive: true });
        vp.addEventListener('touchend', (e) => {
          if (!tracking || !e.changedTouches || !e.changedTouches.length) return;
          tracking = false;
          const dx = e.changedTouches[0].clientX - startX;
          const dy = e.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          this.showGalleryIndex(this.galleryIndex + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }

      if (this.dom.addButton && this.dom.stickyCta && 'IntersectionObserver' in window) {
        this._inlineObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.scrolledPastInline = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          this.updateSticky();
        }, { threshold: 0 });
        this._inlineObserver.observe(this.dom.addButton);
      }
    }

    /* ─── STATE ───────────────────────────────────────────────── */

    isNoDiscount(row) {
      return NO_DISCOUNT_RE.test(row.dataset.variantTitle || '');
    }

    updateScent(row, delta) {
      if (!row) return;
      const variantId = row.dataset.variantId;
      const available = row.dataset.variantAvailable === 'true';
      if (!available) return;

      const currentCount = this.state.scents[variantId] || 0;
      const max = parseInt(row.dataset.variantMax, 10) || 0;
      let next = currentCount + delta;
      if (next < 0) next = 0;
      if (max > 0 && next > max) next = max;

      if (next === 0) { delete this.state.scents[variantId]; }
      else { this.state.scents[variantId] = next; }

      const countEl = row.querySelector('[data-scent-count]');
      if (countEl) countEl.textContent = String(next);

      this.render();
    }

    totalSelected() {
      return Object.values(this.state.scents).reduce((s, n) => s + n, 0);
    }

    // Picked units that are eligible for the tier discount (excludes Mystery).
    discountableSelected() {
      let n = 0;
      this.dom.scentRows.forEach((row) => {
        if (this.isNoDiscount(row)) return;
        n += this.state.scents[row.dataset.variantId] || 0;
      });
      return n;
    }

    // Cart-aware total for tier/progress/meta purposes only. Never used for
    // cart-add payloads (that stays totalSelected — this click's picks only).
    effectiveTotal() {
      return (this.cartCount || 0) + this.discountableSelected();
    }

    tierFromCount(total) {
      let idx = -1;
      this.tiers.forEach((t, i) => { if (total >= t.qty) idx = i; });
      return idx;
    }

    activeTierIndex() {
      const effective = this.effectiveTotal();
      if (effective > 0) return this.tierFromCount(effective);
      if (this.totalSelected() > 0) return -1; // only no-discount items picked
      return this.state.clickedTier;
    }

    computeTotals() {
      const total = this.totalSelected();
      const effective = this.effectiveTotal();
      const tierIdx = this.activeTierIndex();
      const pct = (tierIdx != null && tierIdx >= 0 && this.tiers[tierIdx]) ? (this.tiers[tierIdx].pct || 0) : 0;

      // Gross covers only what THIS click adds — the discount pct comes from
      // the effective (cart-inclusive) tier because checkout applies that
      // pct to ALL eligible units of this product-type at settlement.
      // No-discount units (Mystery) are added at their own price, untouched.
      let discGross = 0;
      let flatGross = 0;
      this.dom.scentRows.forEach((row) => {
        const count = this.state.scents[row.dataset.variantId] || 0;
        if (count === 0) return;
        const price = parseInt(row.dataset.variantPrice, 10) || 0;
        if (this.isNoDiscount(row)) flatGross += price * count;
        else discGross += price * count;
      });

      const gross = discGross + flatGross;
      const net = discGross * (1 - pct / 100) + flatGross;
      return { total, effective, tierIdx, pct, gross, net, saved: Math.max(0, gross - net) };
    }

    /* ─── RENDER ──────────────────────────────────────────────── */

    render() {
      const { total, effective, tierIdx, pct, net, saved } = this.computeTotals();

      this.dom.tierOptions.forEach((opt) => {
        const idx = parseInt(opt.dataset.tierIndex, 10);
        const active = idx === tierIdx;
        opt.classList.toggle('is-active', active);
        // Do NOT force-sync radio.checked here — that would immediately
        // undo the state a user just set by tapping a different tier
        // whenever effectiveTotal drives tierIdx to a different index,
        // making other tier cards look untappable. Radio state is now
        // owned by user taps alone; visual highlight tracks effective
        // total via the .is-active class (see stylesheet).
      });
      this.buyBox.setAttribute('data-tier', tierIdx == null ? 'null' : String(tierIdx));

      // Cart note — "N bars already in your cart" under the step-1 legend.
      if (this.dom.cartNote) {
        if (this.cartCount > 0) {
          const noun = this.cartCount === 1 ? this.unitNoun : this.unitNounPlural;
          this.dom.cartNote.textContent = this.cartCount + ' ' + noun + ' already in your cart';
          this.dom.cartNote.hidden = false;
        } else {
          this.dom.cartNote.hidden = true;
        }
      }

      this.dom.scentRows.forEach((row) => {
        const available = row.dataset.variantAvailable === 'true';
        const count = this.state.scents[row.dataset.variantId] || 0;
        const dec = row.querySelector('[data-scent-dec]');
        const inc = row.querySelector('[data-scent-inc]');
        const max = parseInt(row.dataset.variantMax, 10) || 0;
        if (inc) inc.disabled = !available || (max > 0 && count >= max);
        if (dec) dec.disabled = !available || count <= 0;
      });

      // --- Step-2 progress: clicked tier is the goal until reached, then it rolls forward ---
      // effective (cart + picked) drives the tier ladder so the customer sees
      // the tier they'll actually get at checkout.
      const ci = this.state.clickedTier;
      const clickedTierObj = (ci != null && this.tiers[ci]) ? this.tiers[ci] : null;
      const nextAuto = this.tiers.find((t) => t.qty > effective && t.pct > 0) || null;
      const goalTier = (clickedTierObj && clickedTierObj.pct > 0 && clickedTierObj.qty > effective) ? clickedTierObj : nextAuto;

      let metaText;
      let fill = 0;
      let isActive = false;

      if (effective === 0 && !(clickedTierObj && clickedTierObj.pct > 0)) {
        metaText = 'CHOOSE QTY FIRST';
      } else if (goalTier) {
        const needed = goalTier.qty - effective;
        const noun = (needed === 1 ? this.unitNoun : this.unitNounPlural).toUpperCase();
        metaText = needed + ' ' + noun + ' TO ' + goalTier.pct + '% OFF';
        fill = Math.min(100, (effective / goalTier.qty) * 100);
        isActive = effective > 0;
      } else {
        const topPct = this.tiers.length ? this.tiers[this.tiers.length - 1].pct : 0;
        metaText = "YOU'RE SAVING " + topPct + '%';
        fill = 100;
        isActive = true;
      }

      // Unlock flash: when the goal you were chasing is reached and rolls
      // forward, fill to 100% then settle. Suppress on the initial cart-sync
      // render so the flash never fires from passively picking up existing
      // cart items — only from user picks.
      const goalQty = goalTier ? goalTier.qty : Infinity;
      if (this._lastGoalQty === undefined || this._suppressUnlockFlash) {
        this._lastGoalQty = goalQty;
        this._suppressUnlockFlash = false;
      }
      const justUnlocked = effective > 0 && effective >= this._lastGoalQty && goalQty > this._lastGoalQty;
      this._lastGoalQty = goalQty;

      if (this.dom.progressFill) {
        clearTimeout(this._fillTimer);
        if (justUnlocked) {
          this.dom.progressFill.style.width = '100%';
          this._fillTimer = setTimeout(() => {
            if (this.dom.progressFill) this.dom.progressFill.style.width = fill + '%';
          }, 450);
        } else {
          this.dom.progressFill.style.width = fill + '%';
        }
      }

      if (this.dom.scentMeta) {
        this.dom.scentMeta.textContent = metaText;
        this.dom.scentMeta.style.color = isActive ? 'var(--color-burgundy-deep)' : '';
      }

      if (this.dom.freeGift) {
        this.dom.freeGift.classList.toggle('is-locked', total < 1);
      }

      if (this.dom.subtotalPrice) this.dom.subtotalPrice.textContent = moneyFormat(net);
      if (this.dom.subtotalMeta) {
        this.dom.subtotalMeta.style.color = '';
        if (tierIdx == null) {
          this.dom.subtotalMeta.textContent = 'Pick a quantity above';
        } else if (total === 0) {
          this.dom.subtotalMeta.textContent = 'Pick a quantity above';
        } else if (pct > 0 && saved > 0) {
          this.dom.subtotalMeta.textContent = "YOU'RE SAVING " + pct + '%';
          this.dom.subtotalMeta.style.color = 'var(--color-burgundy-deep)';
        } else if (this.discountableSelected() === 0) {
          this.dom.subtotalMeta.textContent = total + ' ' + (total === 1 ? this.unitNoun : this.unitNounPlural);
        } else {
          const nextTier = this.tiers.find((t) => t.qty > effective);
          if (nextTier && nextTier.pct > 0) {
            const more = nextTier.qty - effective;
            this.dom.subtotalMeta.textContent = 'Add ' + more + ' more to save ' + nextTier.pct + '%';
          } else {
            this.dom.subtotalMeta.textContent = total + ' ' + (total === 1 ? this.unitNoun : this.unitNounPlural);
          }
        }
      }
      if (this.dom.subtotalSaved) {
        if (saved > 0) {
          this.dom.subtotalSaved.textContent = 'SAVED ' + moneyFormat(saved);
          this.dom.subtotalSaved.hidden = false;
        } else {
          this.dom.subtotalSaved.hidden = true;
        }
      }

      if (this.dom.addButton) {
        const noun = total === 1 ? this.unitNoun : this.unitNoun + 's';
        if (tierIdx == null) {
          if (this.dom.addLabel) this.dom.addLabel.textContent = 'Choose a quantity above';
          this.dom.addButton.disabled = true;
        } else if (total === 0) {
          if (this.dom.addLabel) this.dom.addLabel.textContent = 'Add to cart';
          this.dom.addButton.disabled = true;
        } else {
          if (this.dom.addLabel) this.dom.addLabel.textContent = 'Add to cart · ' + total + ' ' + noun + ' →';
          this.dom.addButton.disabled = false;
        }
      }

      this._net = net;
      this._total = total;
      this.updateSticky();
    }

    updateSticky() {
      if (!this.dom.stickyCta) return;
      const total = this._total || 0;
      const show = this.scrolledPastInline && total > 0;
      this.dom.stickyCta.hidden = !show;
      if (!show) return;
      const noun = total === 1 ? this.unitNoun.toUpperCase() : (this.unitNoun + 's').toUpperCase();
      if (this.dom.stickyMeta) this.dom.stickyMeta.textContent = total + ' ' + noun;
      if (this.dom.stickyPrice) this.dom.stickyPrice.textContent = moneyFormat(this._net || 0);
      if (this.dom.stickyAdd) this.dom.stickyAdd.disabled = false;
    }

    /* ─── GALLERY (thumbnail click / swipe → main image) ──────────── */

    showGalleryIndex(idx) {
      if (!this.galleryImages.length) return;
      if (idx < 0) idx = 0;
      if (idx > this.galleryImages.length - 1) idx = this.galleryImages.length - 1;
      this.galleryIndex = idx;
      const img = this.galleryImages[idx];
      this.swapGalleryImage(img.src, img.alt);
    }

    swapGalleryImage(src, alt) {
      if (!src) return;
      const mainImg = this.dom.galleryMain;
      if (!mainImg) return;
      mainImg.style.opacity = '0';
      const preload = new Image();
      preload.onload = () => {
        mainImg.src = src;
        if (alt) mainImg.alt = alt;
        mainImg.removeAttribute('srcset');
        requestAnimationFrame(() => { mainImg.style.opacity = '1'; });
      };
      preload.onerror = () => { mainImg.style.opacity = '1'; };
      preload.src = src;
    }

    /* ─── CART ────────────────────────────────────────────────── */

    // Number of free mesh bags for a given number of bars, from the
    // configured breakpoints (default 3/6/9 → 1-2:1, 3-5:2, 6-8:3, 9+:4).
    bagsForBars(bars) {
      if (bars < 1) return 0;
      let n = 1;
      this.giftBagSteps.forEach((step) => { if (bars >= step) n += 1; });
      return n;
    }

    async addToBag() {
      const total = this.totalSelected();
      if (total < 1) return;

      const soapItems = [];
      this.dom.scentRows.forEach((row) => {
        const available = row.dataset.variantAvailable === 'true';
        const count = this.state.scents[row.dataset.variantId] || 0;
        const id = parseInt(row.dataset.variantId, 10);
        if (!available || count <= 0 || !id) return;
        soapItems.push({ id: id, quantity: count });
      });
      if (soapItems.length === 0) return;

      const giftId = parseInt(this.freeGiftVariantId, 10) || null;

      const btn = this.dom.addButton;
      const stickyBtn = this.dom.stickyAdd;
      const originalLabel = this.dom.addLabel && this.dom.addLabel.textContent;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (this.dom.addLabel) this.dom.addLabel.textContent = 'Adding…';
      stickyBtn && (stickyBtn.disabled = true);

      const hasON = window.ON && window.ON.cart;
      const cartAdd = (items) => hasON && typeof window.ON.cart.add === 'function'
        ? window.ON.cart.add(items)
        : fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ items }) }).then((r) => { if (!r.ok) return r.json().then((e) => { throw new Error(e.description || ('Cart error ' + r.status)); }); return r.json(); });
      const cartGet = () => hasON && typeof window.ON.cart.get === 'function'
        ? window.ON.cart.get()
        : fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const cartChange = (changes) => hasON && typeof window.ON.cart.change === 'function'
        ? window.ON.cart.change(changes)
        : fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(changes) }).then((r) => r.json());
      const openCart = () => { if (window.ON && typeof window.ON.openDrawer === 'function') window.ON.openDrawer('cart'); };

      try {
        // 1. Add the bars.
        await cartAdd(soapItems);

        // 2. Reconcile the complimentary mesh bag quantity to the TOTAL number
        //    of bars now in the cart. The bag carries a _free_gift line-item
        //    property so the cart drawer can lock its stepper. If the customer
        //    previously removed the bag, we honour that and don't re-add it.
        if (giftId) {
          try {
            const cart = await cartGet();
            const items = (cart && cart.items) || [];
            let totalBars = 0;
            let giftLine = null;
            items.forEach((it) => {
              if (this.productId && it.product_id === this.productId) totalBars += it.quantity;
              if (it.variant_id === giftId) giftLine = it;
            });
            const wantBags = this.bagsForBars(totalBars);

            if (giftLine) {
              if (giftLine.quantity !== wantBags) {
                await cartChange({ id: giftLine.key, quantity: wantBags });
              }
            } else if (wantBags > 0) {
              await cartAdd([{ id: giftId, quantity: wantBags, properties: { _free_gift: 'true' } }]);
            }
          } catch (giftErr) {
            console.warn('PDPBuyBox gift reconcile skipped:', giftErr);
          }
        }

        openCart();

        this.state.scents = {};
        this.state.clickedTier = null;
        this.dom.scentRows.forEach((row) => {
          const countEl = row.querySelector('[data-scent-count]');
          if (countEl) countEl.textContent = '0';
        });
        this.render();
      } catch (err) {
        if (this.dom.addLabel && originalLabel) this.dom.addLabel.textContent = originalLabel;
        if (this.dom.subtotalMeta) {
          this.dom.subtotalMeta.textContent = err.message || 'Something went wrong. Please try again.';
          this.dom.subtotalMeta.style.color = 'var(--color-burgundy-deep)';
        }
        console.error('PDPBuyBox addToBag failed:', err);
      } finally {
        btn.removeAttribute('aria-busy');
        this.render();
      }
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR_ROOT).forEach((root) => new PDPBuyBox(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();